<template>
  <Tabs :value="tab" :items="tabs" class="site-navigation-link-picker" @input="setTab">
    <template #page>
      <InputComponent
        v-model="search"
        :placeholder="`${$t('search')}...`"
        class="site-navigation-link-picker__search"
        @keydown.enter.prevent
      />
      <div class="site-navigation-link-picker__list">
        <button
          v-for="page in filteredPages"
          :key="page.id"
          type="button"
          :aria-current="isSelected('page', page.id)"
          :class="[
            'site-navigation-link-picker__item',
            { 'site-navigation-link-picker__item--selected': isSelected('page', page.id) },
          ]"
          @click="$emit('input', { type: 'page', value: page.id }, page.label)"
        >
          <span class="site-navigation-link-picker__item-label">{{ page.label }}</span>
          <span class="site-navigation-link-picker__item-sublabel">{{ page.sublabel }}</span>
          <span class="site-navigation-link-picker__item-check">
            <Icon v-if="isSelected('page', page.id)" icon="fa-pro-light:check" />
          </span>
        </button>
        <p v-if="!filteredPages.length" class="site-navigation-link-picker__empty">{{ $t('noPagesFound') }}</p>
      </div>

      <SelectComponent
        :value="selectedAnchor"
        :items="anchorItems"
        :label="$t('chooseAnchorLink')"
        :emptyLabel="$t('menuItemSelectPageFirst')"
        :disabled="!(targetAnchors && targetAnchors.length)"
        v-tooltip="{ content: anchorTooltip, placement: 'top' }"
        itemStyle="square"
        class="site-navigation-link-picker__anchors"
        @input="setAnchor"
      />
    </template>

    <template #unit>
      <InputComponent
        v-model="search"
        :placeholder="`${$t('search')}...`"
        class="site-navigation-link-picker__search"
        @keydown.enter.prevent
      />
      <div class="site-navigation-link-picker__list">
        <button
          v-for="unit in filteredUnits"
          :key="unit.id"
          type="button"
          :aria-current="isSelected('unit', unit.id)"
          :class="[
            'site-navigation-link-picker__item',
            { 'site-navigation-link-picker__item--selected': isSelected('unit', unit.id) },
          ]"
          @click="$emit('input', { type: 'unit', value: unit.id }, unit.label)"
        >
          <span class="site-navigation-link-picker__item-label">{{ unit.label }}</span>
          <span class="site-navigation-link-picker__item-check">
            <Icon v-if="isSelected('unit', unit.id)" icon="fa-pro-light:check" />
          </span>
        </button>
        <p v-if="!filteredUnits.length" class="site-navigation-link-picker__empty">{{ $t('noUnitsFound') }}</p>
      </div>
    </template>

    <template #url>
      <InputComponent
        :value="value.type === 'url' ? value.value : ''"
        :label="$t('externalUrl')"
        placeholder="https://"
        :sublabel="$t('menuItemUrlSublabel')"
        required
        @input="$ev => $emit('input', { type: 'url', value: $ev })"
      />
    </template>

    <template #none>
      <p class="site-navigation-link-picker__info">{{ $t('menuItemSubmenuDescription') }}</p>
    </template>
  </Tabs>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import SelectComponent from '@kvass/custom-fields/src/Fields/SelectModal'
import Tabs from '@/components/Tabs'

const getThumbnail = thumbnail => {
  if (/^(https|\/)/.test(thumbnail)) return { thumbnail }
  return { icon: /^{.+}$/.test(thumbnail) ? null : thumbnail }
}

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    pages: {
      type: Array,
      default: () => [],
    },
    anchors: {
      type: Object,
      default: () => ({}),
    },
    units: {
      type: Array,
      default: () => [],
    },
    type: String,
    nested: Boolean,
  },
  data() {
    return {
      tab: this.value.type || 'page',
      search: '',
    }
  },
  computed: {
    tabs() {
      return [
        { id: 'page', label: this.$tc('page', 2) },
        { id: 'unit', label: this.$tc('unit', 2), condition: this.type === 'Project' },
        { id: 'url', label: this.$t('externalUrl') },
        { id: 'none', label: this.$t('menuItemSubmenu'), condition: this.nested },
      ].filter(tab => !('condition' in tab) || tab.condition)
    },
    filteredPages() {
      return this.filter(this.pages)
    },
    filteredUnits() {
      return this.filter(this.units)
    },
    targetAnchors() {
      return this.$path([this.value.type, this.value.value], this.anchors)
    },
    selectedAnchor() {
      if (!this.targetAnchors) return
      return this.value.anchor || 'top'
    },
    anchorTooltip() {
      if (!this.targetAnchors || this.targetAnchors.length) return
      return this.$t('menuItemPageHasNoAnchors')
    },
    anchorItems() {
      return [
        { id: 'top', label: this.$t('topOfThePage'), icon: 'fa-pro-light:arrow-up-to-line' },
        ...(this.targetAnchors || []).map(anchor => ({
          id: anchor.key,
          label: anchor.label,
          ...getThumbnail(anchor.thumbnail),
        })),
      ]
    },
  },
  methods: {
    setTab(tab) {
      this.tab = tab
      this.search = ''
      if (tab === 'none') this.$emit('input', { type: 'none' })
    },
    setAnchor(anchor) {
      const { type, value } = this.value
      const block = this.targetAnchors.find(block => block.key === anchor)
      if (block) return this.$emit('input', { type, value, anchor }, block.label)

      const target = [...this.pages, ...this.units].find(item => item.id === value)
      this.$emit('input', { type, value }, target.label)
    },
    isSelected(type, value) {
      return this.value.type === type && this.value.value === value
    },
    filter(items) {
      const search = this.search.toLowerCase()
      return items.filter(item => item.label.toLowerCase().includes(search))
    },
  },
  components: {
    InputComponent,
    SelectComponent,
    Tabs,
  },
}
</script>

<style lang="scss">
.site-navigation-link-picker {
  &__search {
    margin-bottom: 0.75rem;
  }

  &__list {
    display: flex;
    flex-direction: column;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border: none;
    border-bottom: 1px solid var(--border-color);
    background-color: white;
    text-align: left;
    font: inherit;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: var(--lightest-grey);
    }

    &--selected,
    &--selected:hover {
      background-color: css-alpha('primary', 0.1);
    }

    &-label {
      flex-grow: 1;
      overflow-wrap: anywhere;
    }

    &-sublabel {
      font-size: var(--font-size-small);
      opacity: var(--opacity-muted);
    }

    &-check {
      width: 1rem;
      color: var(--primary);
    }
  }

  &__anchors {
    margin-top: 1rem;
  }

  &__empty {
    margin: 0;
    padding: 0.75rem 1rem;
    opacity: var(--opacity-muted);
  }

  &__info {
    margin: 0;
    padding: 1rem;
    border-radius: var(--border-radius);
    background-color: var(--lightest-grey);
  }
}
</style>
